"use client";

import { useState } from "react";
import { Notice, PillGroup, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CurrencyField } from "@/components/tools/finance/shared";
import { StudentLoanResults } from "@/components/tools/finance/student-loan-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { currencyDigits } from "@/lib/tool-kit/finance/currency";
import type {
  Loan,
  Plan,
  PlanKind,
  Strategy,
} from "@/lib/tool-kit/finance/student-loans";

/** English strings. A translated content file overrides these through `ui`. */
export const STUDENT_LOAN_REPAYMENT_CALCULATOR_STRINGS = {
  loansHeading: "Your loans",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  loanNamePlaceholder: "Name (optional)",
  loanDefaultName: "Loan {n}",
  loanNameLabel: "Name of loan {n}",
  loanBalance: "Balance",
  loanRate: "Annual rate (%)",
  removeLoan: "Remove loan {n}",
  remove: "Remove",
  addLoan: "Add a loan",
  settingsHeading: "Schedule settings",
  standardYears: "Level payments, shorter term (years)",
  extendedYears: "Level payments, longer term (years)",
  graduatedYears: "Graduated payments, term (years)",
  stepUp: "Graduated step-up every 24 months (%)",
  payoffHeading: "Paying it off faster",
  chosenPlan: "Schedule to model",
  planStandard: "Level payments, shorter term",
  planExtended: "Level payments, longer term",
  planGraduated: "Graduated payments",
  extra: "Extra per month",
  strategy: "Extra money goes first to",
  strategyAvalanche: "The highest interest rate (avalanche)",
  strategySnowball: "The smallest balance (snowball)",
  modelNote:
    "Interest accrues monthly at the annual rate ÷ 12 from the first payment. In-school interest, grace periods, capitalization and income-driven formulas are not modelled, and a paid-off loan's payment rolls over to the others.",
  planRulesNote:
    "These are schedules built by formula, not named programs. Real repayment plans have their own names, terms and eligibility rules, and those change — check your servicer's terms; for U.S. federal loans the official source is studentaid.gov.",
  resultsHeading: "Results",
  planTableHeading: "The three schedules compared",
  scheduleColumn: "Schedule",
  firstColumn: "First month",
  peakColumn: "Highest month",
  timeColumn: "Time to repay",
  interestColumn: "Total interest",
  paidColumn: "Total paid",
  yearsUnit: "one:{count} year|other:{count} years",
  monthsUnit: "one:{count} month|other:{count} months",
  durationJoin: "{years} {months}",
  timeToRepay: "Time to repay",
  totalInterestStat: "Total interest",
  totalPaidStat: "Total paid",
  interestSaved: "Interest saved by the extra",
  timeSaved: "Time saved by the extra",
  loanTableHeading: "Loan by loan",
  loanColumn: "Loan",
  balanceColumn: "Balance",
  rateColumn: "Rate",
  payoffColumn: "Paid off in month",
  loanInterestColumn: "Interest paid",
  strategyTableHeading: "Where the extra money goes",
  strategyColumn: "Approach",
  strategyNone: "Scheduled payments only",
  needExtra:
    "Enter an extra monthly amount above zero to compare the approaches.",
  chartTitle: "Total balance over time",
  chartXTitle: "Years",
  chartYTitle: "Balance",
  chartLabel: "Total balance chart",
  chartDescription:
    "Line chart of the combined loan balance by year, from {start} at year 0 to zero at the end of repayment.",
  seriesScheduled: "Scheduled payments only",
  seriesExtra: "With the extra",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  graduatedLow:
    "On the graduated schedule the first payments are below one loan's monthly interest, so that balance grows at first. A smaller step-up avoids this.",
  incomplete:
    "A balance was still open after 60 years of payments, so the totals are not shown. Check the rates and terms.",
  needLoans:
    "Enter at least one loan with a balance above zero and a rate of 0 or more.",
  invalidTerm:
    "Enter terms between 1 and 40 years, and a step-up of 0 or more.",
} as const;

export type StudentLoanStrings =
  typeof STUDENT_LOAN_REPAYMENT_CALCULATOR_STRINGS;

interface Row {
  id: number;
  name: string;
  balance: string;
  rate: string;
}

let nextRow = 0;

function makeRow(name: string, balance: string, rate: string): Row {
  nextRow += 1;
  return { id: nextRow, name, balance, rate };
}

const MAX_LOANS = 10;

interface Settings {
  standardYears: string;
  extendedYears: string;
  graduatedYears: string;
  step: string;
}

function parsePlans(settings: Settings): Record<PlanKind, Plan> | null {
  const years = {
    standard: parseNumberInput(settings.standardYears),
    extended: parseNumberInput(settings.extendedYears),
    graduated: parseNumberInput(settings.graduatedYears),
  };
  const step = parseNumberOr(settings.step, 0);
  for (const value of Object.values(years)) {
    if (value === null || value < 1 || value > 40) {
      return null;
    }
  }
  if (step < 0) {
    return null;
  }
  const months = (kind: PlanKind) => Math.round((years[kind] ?? 10) * 12);
  return {
    standard: { kind: "standard", months: months("standard"), step: 0 },
    extended: { kind: "extended", months: months("extended"), step: 0 },
    graduated: {
      kind: "graduated",
      months: months("graduated"),
      step: step / 100,
    },
  };
}

function parseLoans(rows: Row[], names: (n: number) => string): Loan[] {
  const loans: Loan[] = [];
  for (const [index, row] of rows.entries()) {
    const balance = parseNumberInput(row.balance);
    const rate = parseNumberInput(row.rate);
    if (balance !== null && balance > 0 && rate !== null && rate >= 0) {
      const loan: Loan = {
        name: row.name.trim() || names(index + 1),
        balance,
        rate: rate / 100,
      };
      loans.push(loan);
    }
  }
  return loans;
}

function LoanRows({
  rows,
  setRows,
  strings,
}: {
  rows: Row[];
  setRows: (update: (old: Row[]) => Row[]) => void;
  strings: StudentLoanStrings;
}) {
  const fmt = useToolNumber();
  const patch = (id: number, change: Partial<Row>) =>
    setRows((old) =>
      old.map((row) => (row.id === id ? { ...row, ...change } : row))
    );
  return (
    <div className="space-y-3">
      {rows.map((row, index) => (
        <div
          className="grid items-end gap-3 sm:grid-cols-[1.4fr_1fr_1fr_auto]"
          key={row.id}
        >
          <TextField
            id={`sl-name-${row.id}`}
            label={t(strings.loanNameLabel, { n: fmt(index + 1) })}
            onChange={(value) => patch(row.id, { name: value })}
            placeholder={t(strings.loanDefaultName, { n: fmt(index + 1) })}
            value={row.name}
          />
          <TextField
            id={`sl-balance-${row.id}`}
            label={strings.loanBalance}
            numeric
            onChange={(value) => patch(row.id, { balance: value })}
            value={row.balance}
          />
          <TextField
            id={`sl-rate-${row.id}`}
            label={strings.loanRate}
            numeric
            onChange={(value) => patch(row.id, { rate: value })}
            value={row.rate}
          />
          <button
            aria-label={t(strings.removeLoan, { n: fmt(index + 1) })}
            className={BUTTON_SMALL}
            disabled={rows.length <= 1}
            onClick={() =>
              setRows((old) => old.filter((entry) => entry.id !== row.id))
            }
            type="button"
          >
            {strings.remove}
          </button>
        </div>
      ))}
      <button
        className={BUTTON_SECONDARY}
        disabled={rows.length >= MAX_LOANS}
        onClick={() => setRows((old) => [...old, makeRow("", "", "")])}
        type="button"
      >
        {strings.addLoan}
      </button>
    </div>
  );
}

export default function StudentLoanRepaymentCalculatorWidget() {
  const strings = useToolUi(STUDENT_LOAN_REPAYMENT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [currency, setCurrency] = useState("USD");
  const [rows, setRows] = useState<Row[]>(() => [
    makeRow("", "12000", "4.5"),
    makeRow("", "8500", "6.8"),
    makeRow("", "5000", "3.9"),
  ]);
  const [settings, setSettings] = useState<Settings>({
    standardYears: "10",
    extendedYears: "25",
    graduatedYears: "10",
    step: "20",
  });
  const [kind, setKind] = useState<PlanKind>("standard");
  const [extra, setExtra] = useState("200");
  const [strategy, setStrategy] =
    useState<Exclude<Strategy, "none">>("avalanche");
  const setSetting = (patch: Partial<Settings>) =>
    setSettings((old) => ({ ...old, ...patch }));

  const plans = parsePlans(settings);
  const loans = parseLoans(rows, (n) =>
    t(strings.loanDefaultName, { n: fmt(n) })
  );

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.loansHeading}</h2>
        <div className="max-w-xs">
          <CurrencyField
            id="sl-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
        </div>
        <LoanRows rows={rows} setRows={setRows} strings={strings} />
      </section>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.settingsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="sl-standard-years"
            label={strings.standardYears}
            numeric
            onChange={(value) => setSetting({ standardYears: value })}
            value={settings.standardYears}
          />
          <TextField
            id="sl-extended-years"
            label={strings.extendedYears}
            numeric
            onChange={(value) => setSetting({ extendedYears: value })}
            value={settings.extendedYears}
          />
          <TextField
            id="sl-graduated-years"
            label={strings.graduatedYears}
            numeric
            onChange={(value) => setSetting({ graduatedYears: value })}
            value={settings.graduatedYears}
          />
          <TextField
            id="sl-step"
            label={strings.stepUp}
            numeric
            onChange={(value) => setSetting({ step: value })}
            value={settings.step}
          />
        </div>
        <h3 className={LABEL}>{strings.payoffHeading}</h3>
        <PillGroup
          label={strings.chosenPlan}
          name="sl-plan"
          onChange={setKind}
          options={[
            { value: "standard", label: strings.planStandard },
            { value: "extended", label: strings.planExtended },
            { value: "graduated", label: strings.planGraduated },
          ]}
          value={kind}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="sl-extra"
            label={strings.extra}
            numeric
            onChange={setExtra}
            value={extra}
          />
        </div>
        <PillGroup
          label={strings.strategy}
          name="sl-strategy"
          onChange={setStrategy}
          options={[
            { value: "avalanche", label: strings.strategyAvalanche },
            { value: "snowball", label: strings.strategySnowball },
          ]}
          value={strategy}
        />
        <p className={HINT}>{strings.modelNote}</p>
        <p className={HINT}>{strings.planRulesNote}</p>
      </section>
      {plans && loans.length > 0 ? (
        <StudentLoanResults
          currency={currency}
          digits={currencyDigits(currency)}
          extra={Math.max(0, parseNumberOr(extra, 0))}
          kind={kind}
          loans={loans}
          plans={plans}
          strategy={strategy}
          strings={strings}
        />
      ) : (
        <Notice>{plans ? strings.needLoans : strings.invalidTerm}</Notice>
      )}
    </div>
  );
}
